/**
 * Schema v2 support_tickets — defaults lazy para docs antiguos.
 * Espejo de server/utils/supportTicketSchema.js
 */

export const PRIORITIES = ['p1', 'p2', 'p3', 'p4'];

export const PRIORITY_CONFIG = {
  p1: { label: 'P1 Crítica', color: '#DC2626', bg: 'rgba(220, 38, 38, 0.1)' },
  p2: { label: 'P2 Alta', color: '#EA580C', bg: 'rgba(234, 88, 12, 0.1)' },
  p3: { label: 'P3 Media', color: '#331D40', bg: 'rgba(51, 29, 64, 0.08)' },
  p4: { label: 'P4 Baja', color: '#64748B', bg: 'rgba(100, 116, 139, 0.12)' }
};

export const STATUS_CONFIG = {
  open: { label: 'Abierto', color: '#FF3364', bg: 'rgba(255, 51, 100, 0.1)' },
  in_progress: { label: 'En atención', color: '#3B82F6', bg: 'rgba(59, 130, 246, 0.1)' },
  escalated: { label: 'Escalado', color: '#7C3AED', bg: 'rgba(124, 58, 237, 0.12)' },
  waiting_client: { label: 'Esperando', color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.12)' },
  resolved: { label: 'Resuelto', color: '#10B981', bg: 'rgba(16, 185, 129, 0.1)' },
  closed: { label: 'Cerrado', color: '#64748B', bg: 'rgba(100, 116, 139, 0.12)' }
};

export const CATEGORIES = {
  'gws-user': {
    label: 'GWS · Usuario',
    subs: {
      gmail: 'Gmail',
      drive: 'Drive',
      calendar: 'Calendar',
      meet: 'Meet',
      docs: 'Docs / Sheets / Slides',
      chat: 'Chat',
      forms: 'Forms',
      sites: 'Sites',
      mobile: 'Móvil',
      'auth-2fa': 'Auth / 2FA'
    }
  },
  'gws-admin': {
    label: 'GWS · Admin',
    subs: {
      'users-groups': 'Usuarios y grupos',
      'domains-dns': 'Dominios / DNS',
      'security-policies': 'Seguridad',
      'devices-chrome': 'Dispositivos / Chrome',
      'apps-services': 'Apps y servicios',
      'audit-compliance': 'Auditoría',
      migration: 'Migración',
      'reports-admin': 'Reportes admin'
    }
  },
  'gemini-enterprise': {
    label: 'Gemini Enterprise',
    subs: {
      'gemini-app': 'Gemini App',
      'gemini-workspace': 'Gemini en Workspace',
      'gemini-admin': 'Admin IA',
      'gemini-data-governance': 'Gobierno de datos',
      'gemini-api-vertex': 'API / Vertex',
      'gemini-training-enablement': 'Capacitación'
    }
  },
  plexarian: {
    label: 'Plexarian',
    subs: {
      'licenses-contract': 'Licencias / contrato',
      'billing-invoice': 'Facturación',
      onboarding: 'Onboarding',
      'portal-access': 'Acceso portal'
    }
  }
};

export const INGRESS_CHANNELS = {
  portal: 'Portal',
  email: 'Correo',
  whatsapp: 'WhatsApp',
  phone: 'Teléfono',
  internal: 'Interno'
};

export function defaultCategoryFromRole(role) {
  return role === 'admin' ? 'gws-admin' : 'gws-user';
}

export function normalizePriority(priority) {
  if (PRIORITIES.includes(priority)) return priority;
  return 'p3';
}

export function normalizeSupportTier(tier) {
  const n = Number(tier);
  if (n === 1 || n === 2 || n === 3) return n;
  return 1;
}

/** Flujo completo (T2/T3 incluye escalado). */
export const STATUS_FLOW_FULL = [
  'open',
  'in_progress',
  'escalated',
  'waiting_client',
  'resolved',
  'closed'
];

/** Stepper según nivel: «Escalado» solo visible en T2/T3. */
export function statusFlowForTier(tier) {
  if (normalizeSupportTier(tier) >= 2) return STATUS_FLOW_FULL;
  return ['open', 'in_progress', 'waiting_client', 'resolved', 'closed'];
}

/** Si el ticket quedó en escalado con T1, mostrar progreso en «En atención». */
export function effectiveStatusInFlow(status, flow) {
  const s = status || 'open';
  if (flow.includes(s)) return s;
  if (s === 'escalated' && flow.includes('in_progress')) return 'in_progress';
  return flow.includes(s) ? s : flow[0] || 'open';
}

export function tierShowsEscalatedStatus(tier) {
  return normalizeSupportTier(tier) >= 2;
}

export function inferIngressChannel(ticket = {}) {
  if (ticket.ingressChannel && INGRESS_CHANNELS[ticket.ingressChannel]) {
    return ticket.ingressChannel;
  }
  if (ticket.source === 'support_group') return 'email';
  if (ticket.source === 'support_portal') return 'portal';
  if (ticket.source === 'internal') return 'internal';
  if (ticket.source === 'whatsapp') return 'whatsapp';
  if (ticket.source === 'phone') return 'phone';
  return 'portal';
}

export function buildCreateFields({
  role = 'user',
  question = '',
  priority,
  category,
  subcategory,
  supportTier,
  ingressChannel,
  source
} = {}) {
  const cat = category || defaultCategoryFromRole(role);
  return {
    title: String(question || '').trim(),
    priority: normalizePriority(priority),
    category: getActiveCategories()[cat] ? cat : defaultCategoryFromRole(role),
    subcategory: subcategory || null,
    supportTier: normalizeSupportTier(supportTier),
    ingressChannel: ingressChannel || inferIngressChannel({ source, ingressChannel })
  };
}

export function normalizeSupportTicket(ticket = {}) {
  const role = ticket.role || 'user';
  const question = ticket.question || '';
  return {
    ...ticket,
    title: ticket.title || question,
    priority: normalizePriority(ticket.priority),
    category: ticket.category && getActiveCategories()[ticket.category]
      ? ticket.category
      : defaultCategoryFromRole(role),
    subcategory: ticket.subcategory || null,
    supportTier: normalizeSupportTier(ticket.supportTier),
    ingressChannel: inferIngressChannel(ticket),
    status: ticket.status || 'open'
  };
}

export function isOpenStatus(status) {
  return !['resolved', 'closed'].includes(status || 'open');
}

export const DEFAULT_CATEGORIES = { ...CATEGORIES };

let activeCategories = CATEGORIES;

export function setActiveCategories(cats) {
  if (cats && typeof cats === 'object' && Object.keys(cats).length) {
    activeCategories = cats;
  }
}

export function getActiveCategories() {
  return activeCategories;
}

export function subcategoryLabel(category, subcategory, cats = activeCategories) {
  if (!subcategory) return null;
  return cats[category]?.subs?.[subcategory] || subcategory;
}

export function categoryLabel(category, cats = activeCategories) {
  return cats[category]?.label || category || '—';
}

export function ingressLabel(channel) {
  return INGRESS_CHANNELS[channel] || channel || '—';
}
